/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import { BufferGeometry, Color, Float32BufferAttribute, Mesh, ShaderMaterial } from 'three';

import { FloorGameWebGLVariant } from '@pkg/webgl/types';

import PerformanceProfile from '../../../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

const CONFIG_UP = [
    {
        // Tier1
        colorA: '#696969',
        colorB: '#323232',
        gradientIntensity: 0.75,
        colorIntensity: 2.0,
        rotate: 0.944,
    },
    {
        // Tier2
        colorA: '#101c8a',
        colorB: '#323232',
        gradientIntensity: 1.0,
        colorIntensity: 2.0,
        rotate: 0.944,
    },
    {
        // Tier3
        colorA: '#47108a',
        colorB: '#323232',
        gradientIntensity: 1.0,
        colorIntensity: 2.0,
        rotate: 0.944,
    },
    {
        // Tier4
        colorA: '#8a6810',
        colorB: '#323232',
        gradientIntensity: 1.0,
        colorIntensity: 2.0,
        rotate: 0.944,
    },
    {
        // Tier5
        colorA: '#8A6810',
        colorB: '#323232',
        gradientIntensity: 1.0,
        colorIntensity: 2.0,
        rotate: 0.944,
    },
];

const CONFIG_DOWN = {
    colorA: '#8a1010',
    colorB: '#323232',
    gradientIntensity: 1.0,
    colorIntensity: 1.0,
    rotate: 0.944,
};

let GUI_UP;
let GUI_DOWN;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI_UP = PerformanceProfile.GUI.floorGameUp.addFolder({
        title: `Gradient Background`,
        expanded: false,
    });

    GUI_UP.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('GradientBackground', CONFIG_UP);
    });

    for (let i = 0; i < CONFIG_UP.length; i++) {
        const folder = GUI_UP.addFolder({
            title: `Tier ${i + 1}`,
            expanded: false,
        });

        folder.addInput(CONFIG_UP[i], 'colorA');
        folder.addInput(CONFIG_UP[i], 'colorB');
        folder.addInput(CONFIG_UP[i], 'gradientIntensity', { min: 0, max: 2, step: 0.0001 });
        folder.addInput(CONFIG_UP[i], 'colorIntensity', { min: 0, max: 3, step: 0.0001 });
        folder.addInput(CONFIG_UP[i], 'rotate', { min: -Math.PI * 0.5, max: Math.PI * 0.5, step: 0.0001 });
    }

    GUI_DOWN = PerformanceProfile.GUI.floorGameDown.addFolder({
        title: `Gradient Background`,
        expanded: false,
    });

    GUI_DOWN.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('GradientBackground', CONFIG_DOWN);
    });

    GUI_DOWN.addInput(CONFIG_DOWN, 'colorA');
    GUI_DOWN.addInput(CONFIG_DOWN, 'colorB');
    GUI_DOWN.addInput(CONFIG_DOWN, 'gradientIntensity', { min: 0, max: 2, step: 0.0001 });
    GUI_DOWN.addInput(CONFIG_DOWN, 'colorIntensity', { min: 0, max: 3, step: 0.0001 });
    GUI_DOWN.addInput(CONFIG_DOWN, 'rotate', { min: -Math.PI * 0.5, max: Math.PI * 0.5, step: 0.0001 });
};

if (PerformanceProfile.devMode) addGUI();

interface CardGradientBackgroundOptions {
    type: FloorGameWebGLVariant;
    tier: number;
}

export class CardGradientBackground extends Mesh {
    type: FloorGameWebGLVariant;
    tier: number;

    constructor({ type, tier }: CardGradientBackgroundOptions) {
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
        geometry.setAttribute('uv', new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));

        const config = type === FloorGameWebGLVariant.TierUp ? CONFIG_UP[tier] : CONFIG_DOWN;

        const material = new ShaderMaterial({
            uniforms: {
                uColorA: { value: new Color(config.colorA) },
                uColorB: { value: new Color(config.colorB) },
                uTime: { value: 0.0 },
                uGradientIntensity: { value: config.gradientIntensity },
                uColorIntensity: { value: config.colorIntensity },
                uRotate: { value: config.rotate },
            },
            vertexShader,
            fragmentShader,
            depthTest: false,
        });

        super(geometry, material);

        this.type = type;
        this.tier = tier;

        if (PerformanceProfile.devMode) {
            if (this.type === FloorGameWebGLVariant.TierUp) {
                GUI_UP.on('change', this.updateUniforms.bind(this));
            } else if (this.type === FloorGameWebGLVariant.TierDown) {
                GUI_DOWN.on('change', this.updateUniforms.bind(this));
            }
        }
    }

    animateIn(delay: number) {
        if (this.type === FloorGameWebGLVariant.TierUp) {
            gsap.fromTo(
                this.material.uniforms.uTime,
                {
                    value: 0,
                },
                {
                    value: delay,
                    duration: 3.5,
                    ease: 'power2.inOut',
                },
            );
        } else if (this.type === FloorGameWebGLVariant.TierDown) {
            gsap.fromTo(
                this.material.uniforms.uTime,
                {
                    value: 2.3,
                },
                {
                    value: 1.0,
                    duration: 1.7,
                    delay: delay,
                    ease: 'power2.inOut',
                },
            );
        }
    }

    update() {
        // console.log(this.material.uniforms.uTime.value);
        // this.material.uniforms.uTime.value += 0.01;
    }

    updateUniforms() {
        const config = this.type === FloorGameWebGLVariant.TierUp ? CONFIG_UP[this.tier] : CONFIG_DOWN;

        this.material.uniforms.uColorA.value.setStyle(config.colorA);
        this.material.uniforms.uColorB.value.setStyle(config.colorB);

        this.material.uniforms.uGradientIntensity.value = config.gradientIntensity;
        this.material.uniforms.uColorIntensity.value = config.colorIntensity;
        this.material.uniforms.uRotate.value = config.rotate;
    }

    delete() {
        this.geometry.dispose();
        this.material.dispose();
    }
}
